/**
 * Open-redirect defense for deep-link preservation across the login round-trip. D-06.
 *
 * Attack vectors rejected (return "/"):
 *   - `undefined` / non-string / empty string
 *   - protocol-relative:  `//evil.com/path`
 *   - triple slash:       `///evil.com/path`
 *   - backslash variant:  `/\evil.com`
 *   - encoded protocol-relative: `/%2Fevil.com`
 *   - absolute URL:       `https://evil.com`, `http://evil.com`
 *   - scheme URL:         `javascript:alert(1)`, `data:text/html,...`
 *   - whitespace:         ` /foo`, `/foo bar`, `/foo\tbar`, `/foo\nbar`
 *
 * Legitimate inputs passed through unchanged:
 *   - `/`
 *   - `/foo`, `/foo/bar`
 *   - `/foo/bar?baz=1`
 *   - `/employees/42#tab-3`
 *   - `/login?redirect=%2Fdashboard`
 *
 * Regex: starts with `/`, not followed by `/`, no whitespace characters.
 * Encoded-slash + backslash secondary checks close the `/%2F` / `/\` gaps that
 * the regex alone lets through.
 */
const SAFE_RELATIVE_PATH = /^\/(?!\/)[^\s]*$/;

export function safeRedirectPath(candidate: string | undefined): string {
  if (typeof candidate !== "string" || candidate.length === 0) return "/";
  if (!SAFE_RELATIVE_PATH.test(candidate)) return "/";
  if (candidate.startsWith("/%2F") || candidate.startsWith("/\\")) return "/";
  return candidate;
}

/**
 * Split a stored redirect target ("/approvals?tab=pending") into the router's
 * { to, search } shape. TanStack Router's `navigate({ to })` treats the whole
 * string as a pathname and 404s when it embeds a "?", so the query must be
 * parsed out into a search object. Returns `search: undefined` when absent.
 */
export function splitRedirectTarget(target: string): {
  to: string;
  search: Record<string, string> | undefined;
} {
  const qIdx = target.indexOf("?");
  if (qIdx === -1) return { to: target, search: undefined };
  const to = target.slice(0, qIdx);
  const search: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(target.slice(qIdx + 1))) search[k] = v;
  return { to, search };
}
